import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import StorageBar, { fmt } from '../components/ui/StorageBar';

/* ── Clipboard fallback for non-HTTPS contexts ────────────────────────── */
const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return Promise.resolve();
};

/* ── Custom confirm dialog ────────────────────────────────────────────────── */
const ConfirmDialog = ({ open, title, body, confirmLabel = 'Confirm', confirmClass = 'bg-red-600 hover:bg-red-700', onConfirm, onCancel }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onCancel} />
            <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6">
                <h3 className="text-sm font-bold text-slate-800 mb-3">{title}</h3>
                <div className="text-sm text-slate-500 mb-5">{body}</div>
                <div className="flex items-center gap-3 justify-end">
                    <button onClick={onCancel} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className={`px-4 py-2 text-sm font-bold text-white rounded-lg transition ${confirmClass}`}>
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ── Dropdown ─────────────────────────────────────────────────────────────── */
const Dropdown = ({ value, onChange, options }) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
    const ref = useRef(null);
    const btnRef = useRef(null);
    useEffect(() => {
        const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    const handleToggle = () => {
        if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
        }
        setOpen(o => !o);
    };
    const selected = options.find(o => o.value === value);
    return (
        <div ref={ref}>
            <button
                ref={btnRef}
                type="button"
                onClick={handleToggle}
                className="flex items-center justify-between gap-2 px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm bg-white hover:border-slate-400 focus:outline-none transition min-w-[90px]"
            >
                <span className="text-slate-700 font-medium">{selected?.label ?? value}</span>
                <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div
                    style={{ position: 'fixed', top: coords.top, left: coords.left, minWidth: coords.width, zIndex: 9999 }}
                    className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
                >
                    {options.map(o => (
                        <button
                            key={o.value}
                            type="button"
                            onClick={() => { onChange(o.value); setOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-sm transition
                                ${value === o.value ? 'bg-slate-100 text-slate-800 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const PLAN_OPTIONS = [
    { value: 'free',  label: 'Free'  },
    { value: 'basic', label: 'Basic' },
    { value: 'pro',   label: 'Pro'   },
];

// Mirrors backend planQuotaMax constants (GB values)
const PLAN_QUOTA_GB = { free: 100, basic: 500, pro: 2048 };

/* ── RegisterUserForm ─────────────────────────────────────────────────────── */
const RegisterUserForm = ({ token, addNotification }) => {
    const [form, setForm] = useState({ username: '', email: '', password: '' });
    const [submitting, setSubmitting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        setShowConfirm(true);
    };

    const handleConfirmed = async () => {
        setShowConfirm(false);
        setSubmitting(true);
        try {
            await api.registerUser(form);
            addNotification?.({ type: 'success', title: 'User created', message: `Account for "${form.username}" created.` });
            setForm({ username: '', email: '', password: '' });
        } catch {
            addNotification?.({ type: 'error', title: 'Failed to create user', message: 'Check the details and try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                        { key: 'username', label: 'Username',  type: 'text' },
                        { key: 'email',    label: 'Email',     type: 'email' },
                        { key: 'password', label: 'Password',  type: 'password', min: 6 },
                    ].map(({ key, label, type, min }) => (
                        <div key={key}>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">{label}</label>
                            <input
                                type={type}
                                value={form[key]}
                                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                                required
                                minLength={min}
                            />
                        </div>
                    ))}
                </div>
                <div className="pt-1">
                    <button
                        type="submit"
                        disabled={submitting}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-bold rounded-lg transition"
                    >
                        {submitting ? 'Creating…' : 'Create user'}
                    </button>
                </div>
            </form>

            {showConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowConfirm(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6">
                        <h3 className="text-sm font-bold text-slate-800 mb-3">Create new user account?</h3>
                        <div className="space-y-2 mb-5">
                            <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                                <span className="text-slate-400 text-xs font-semibold">Username</span>
                                <p className="text-slate-800 font-medium">{form.username}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                                <span className="text-slate-400 text-xs font-semibold">Email</span>
                                <p className="text-slate-800 font-medium">{form.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 justify-end">
                            <button onClick={() => setShowConfirm(false)} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition">
                                Cancel
                            </button>
                            <button onClick={handleConfirmed} className="px-4 py-2 text-sm font-bold text-white bg-[#895129] hover:bg-[#660F09] rounded-lg transition">
                                Create user
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const EXPIRY_OPTIONS = [
    { value: 1,     label: '1 day'    },
    { value: 7,     label: '7 days'   },
    { value: 30,    label: '30 days'  },
    { value: 36500, label: 'No expiry' },
];

const inviteStatusStyle = (status) => {
    switch (status) {
        case 'used':    return 'text-green-700 bg-green-50';
        case 'expired': return 'text-red-600 bg-red-50';
        default:        return 'text-blue-700 bg-blue-50';
    }
};

const fmtExpiry = (dateStr) => {
    const d = new Date(dateStr);
    if (d.getFullYear() > 2100) return 'Never';
    return d.toLocaleDateString();
};

/* ── InvitesSection ──────────────────────────────────────────────────────── */
/* ── Pending Deletions Section ─────────────────────────────────────────── */
const PendingDeletionsSection = ({ addNotification }) => {
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState({});
    const [restoreConfirm, setRestoreConfirm] = useState(null); // user object or null
    const [restoreText, setRestoreText] = useState('');
    const [purgeConfirm, setPurgeConfirm] = useState(null); // user object or null
    const [purgeText, setPurgeText] = useState('');
    const [purging, setPurging] = useState({});

    useEffect(() => {
        api.getPendingDeletions()
            .then(data => setPending(data.pending_deletions || []))
            .catch(() => addNotification?.({ type: 'error', message: 'Failed to load pending deletions.' }))
            .finally(() => setLoading(false));
    }, []);

    const handleCancel = async (u) => {
        setRestoreConfirm(null);
        setRestoreText('');
        setCancelling(c => ({ ...c, [u.id]: true }));
        try {
            await api.cancelDeletion(u.id);
            setPending(prev => prev.filter(row => row.id !== u.id));
            addNotification?.({ type: 'success', title: 'Deletion cancelled', message: `${u.username}'s account has been restored.` });
        } catch {
            addNotification?.({ type: 'error', title: 'Failed', message: `Could not cancel deletion for ${u.username}.` });
        } finally {
            setCancelling(c => ({ ...c, [u.id]: false }));
        }
    };

    const handlePurge = async (u) => {
        setPurgeConfirm(null);
        setPurgeText('');
        setPurging(p => ({ ...p, [u.id]: true }));
        try {
            await api.purgeUserNow(u.id);
            setPending(prev => prev.filter(row => row.id !== u.id));
            addNotification?.({ type: 'success', title: 'Account purged', message: `${u.username} has been permanently deleted.` });
        } catch (err) {
            addNotification?.({ type: 'error', title: 'Purge failed', message: err.message || `Could not purge ${u.username}.` });
        } finally {
            setPurging(p => ({ ...p, [u.id]: false }));
        }
    };

    if (loading) {
        return (
            <div className="p-10 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" />
            </div>
        );
    }

    if (pending.length === 0) {
        return (
            <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <p className="text-sm text-slate-400">No pending account deletions.</p>
            </div>
        );
    }

    const expectedText = restoreConfirm ? `restore ${restoreConfirm.username}` : '';
    const expectedPurgeText = purgeConfirm ? `permanently delete ${purgeConfirm.username}` : '';

    return (
        <div className="space-y-3">
            <p className="text-xs text-slate-400 px-1">
                Accounts below are locked out and scheduled for permanent deletion (files, data, and user record).
                Cancel to restore access.
            </p>
            {pending.map(u => {
                const deletionDate = new Date(u.scheduled_deletion_at);
                const daysLeft = Math.max(0, Math.ceil((deletionDate - Date.now()) / (1000 * 60 * 60 * 24)));
                return (
                    <div key={u.id} className="flex items-center justify-between gap-4 rounded-xl border border-red-100 bg-red-50/50 px-4 py-3">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate">{u.username}</p>
                            <p className="text-xs text-slate-400 truncate">{u.email}</p>
                            <p className="text-xs text-red-500 mt-0.5">
                                {daysLeft === 0
                                    ? 'Purge imminent'
                                    : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining — purges ${deletionDate.toLocaleDateString()}`
                                }
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 flex-shrink-0">
                            <button
                                onClick={() => { setRestoreConfirm(u); setRestoreText(''); }}
                                disabled={cancelling[u.id]}
                                className="px-3 py-1.5 text-xs font-bold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 disabled:opacity-50 rounded-lg transition"
                            >
                                {cancelling[u.id] ? 'Restoring…' : 'Cancel & Restore'}
                            </button>
                            <button
                                onClick={() => { setPurgeConfirm(u); setPurgeText(''); }}
                                disabled={purging[u.id]}
                                className="px-3 py-1.5 text-xs font-bold text-red-600 bg-red-100 hover:bg-red-200 disabled:opacity-50 rounded-lg transition"
                            >
                                {purging[u.id] ? 'Deleting…' : 'Delete Now'}
                            </button>
                        </div>
                    </div>
                );
            })}

            {/* ── Restore confirmation modal ── */}
            {restoreConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => { setRestoreConfirm(null); setRestoreText(''); }} />
                    <div className="relative bg-white rounded-2xl shadow-2xl border border-emerald-200 w-full max-w-md p-6 sm:p-8">
                        <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-emerald-100 mb-5">
                            <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 text-center">Restore this account?</h3>
                        <p className="text-sm text-slate-500 text-center mt-2 leading-relaxed">
                            This will cancel the scheduled deletion for <strong className="text-slate-700">{restoreConfirm.username}</strong> and
                            immediately restore their access. They will be able to log in again.
                        </p>
                        <div className="mt-5">
                            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                Type <span className="font-mono bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">restore {restoreConfirm.username}</span> to confirm
                            </label>
                            <input
                                type="text"
                                value={restoreText}
                                onChange={e => setRestoreText(e.target.value)}
                                placeholder={`restore ${restoreConfirm.username}`}
                                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition"
                                autoComplete="off"
                                spellCheck={false}
                            />
                        </div>
                        <div className="mt-5 space-y-3">
                            <button
                                disabled={restoreText !== expectedText}
                                onClick={() => handleCancel(restoreConfirm)}
                                className="w-full px-5 py-3 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed rounded-xl transition shadow-sm"
                            >
                                Restore account
                            </button>
                            <button
                                onClick={() => { setRestoreConfirm(null); setRestoreText(''); }}
                                className="w-full px-5 py-3 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Purge confirmation modal ── */}
            {purgeConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => { setPurgeConfirm(null); setPurgeText(''); }} />
                    <div className="relative bg-white rounded-2xl shadow-2xl border border-red-200 w-full max-w-md p-6 sm:p-8">
                        <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-red-100 mb-5">
                            <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 text-center">Permanently delete this account?</h3>
                        <p className="text-sm text-slate-500 text-center mt-2 leading-relaxed">
                            This will <strong className="text-red-600">immediately and permanently</strong> delete
                            <strong className="text-slate-700"> {purgeConfirm.username}</strong>'s account,
                            including all files, data, and storage. This action cannot be undone.
                        </p>
                        <div className="mt-5">
                            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                Type <span className="font-mono bg-red-50 text-red-700 px-1.5 py-0.5 rounded">permanently delete {purgeConfirm.username}</span> to confirm
                            </label>
                            <input
                                type="text"
                                value={purgeText}
                                onChange={e => setPurgeText(e.target.value)}
                                placeholder={`permanently delete ${purgeConfirm.username}`}
                                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition"
                                autoComplete="off"
                                spellCheck={false}
                            />
                        </div>
                        <div className="mt-5 space-y-3">
                            <button
                                disabled={purgeText !== expectedPurgeText}
                                onClick={() => handlePurge(purgeConfirm)}
                                className="w-full px-5 py-3 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed rounded-xl transition shadow-sm"
                            >
                                Permanently delete account
                            </button>
                            <button
                                onClick={() => { setPurgeConfirm(null); setPurgeText(''); }}
                                className="w-full px-5 py-3 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const InvitesSection = ({ token, addNotification }) => {
    const [email, setEmail] = useState('');
    const [expiryDays, setExpiryDays] = useState(7);
    const [planTier, setPlanTier] = useState('free');
    const [submitting, setSubmitting] = useState(false);
    const [generatedLink, setGeneratedLink] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [invites, setInvites] = useState([]);
    const [loadingInvites, setLoadingInvites] = useState(true);
    const [copied, setCopied] = useState(false);
    const [copiedRow, setCopiedRow] = useState(null);
    const [showInviteConfirm, setShowInviteConfirm] = useState(false);
    const [revokeConfirm, setRevokeConfirm] = useState(null); // invite object or null

    const loadInvites = useCallback(() => {
        api.listInvites()
            .then(data => setInvites(data.invites ?? []))
            .catch(() => {})
            .finally(() => setLoadingInvites(false));
    }, [token]);

    useEffect(() => { loadInvites(); }, [loadInvites]);

    const handleGenerateSubmit = (e) => {
        e.preventDefault();
        setShowInviteConfirm(true);
    };

    const handleGenerateConfirmed = async () => {
        setShowInviteConfirm(false);
        setSubmitting(true);
        setGeneratedLink('');
        try {
            const data = await api.createInvite({ email, expires_in_days: expiryDays, plan_tier: planTier });
            setGeneratedLink(data.link);
            setEmailSent(data.email_sent);
            addNotification?.({ type: 'success', title: 'Invite created', message: 'Invite link generated.' });
            loadInvites();
        } catch {
            addNotification?.({ type: 'error', title: 'Failed', message: 'Could not create invite.' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRevokeConfirmed = async () => {
        const inv = revokeConfirm;
        setRevokeConfirm(null);
        try {
            await api.revokeInvite(inv.id);
            setInvites(prev => prev.filter(i => i.id !== inv.id));
            addNotification?.({ type: 'success', title: 'Invite revoked', message: 'The invite link has been invalidated.' });
        } catch {
            addNotification?.({ type: 'error', title: 'Revoke failed', message: 'Could not revoke the invite.' });
        }
    };

    const handleCopy = () => {
        copyToClipboard(generatedLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="space-y-6">
            {/* Generate form */}
            <form onSubmit={handleGenerateSubmit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                    <div className="sm:col-span-1">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Email (optional)</label>
                        <input
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="Leave blank for generic link"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Plan</label>
                        <Dropdown value={planTier} onChange={setPlanTier} options={PLAN_OPTIONS} />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Expires</label>
                        <Dropdown value={expiryDays} onChange={setExpiryDays} options={EXPIRY_OPTIONS} />
                    </div>
                    <div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-2 bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-bold rounded-lg transition"
                        >
                            {submitting ? 'Generating…' : 'Generate invite'}
                        </button>
                    </div>
                </div>

                {generatedLink && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <p className="text-xs font-bold text-slate-600">Invite Link</p>
                            {emailSent && (
                                <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Email sent</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                readOnly
                                value={generatedLink}
                                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white font-mono"
                            />
                            <button
                                type="button"
                                onClick={handleCopy}
                                className="px-3 py-2 bg-slate-700 hover:bg-slate-800 text-white text-xs font-bold rounded-lg transition whitespace-nowrap"
                            >
                                {copied ? 'Copied!' : 'Copy link'}
                            </button>
                        </div>
                    </div>
                )}
            </form>

            {/* Invites table */}
            {loadingInvites ? (
                <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600" />
                </div>
            ) : invites.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No invites yet.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-100 bg-slate-50">
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Link</th>
                                <th className="px-4 py-3">Expires</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Created</th>
                                <th className="px-4 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {invites.map(inv => (
                                <tr key={inv.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 text-slate-700">
                                        {inv.email || <span className="text-slate-400 italic">Generic</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        {inv.status === 'pending' && inv.link ? (
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-xs text-slate-400 font-mono" title={inv.link}>
                                                    {'…' + inv.link.slice(-10)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        copyToClipboard(inv.link);
                                                        setCopiedRow(inv.id);
                                                        setTimeout(() => setCopiedRow(null), 2000);
                                                    }}
                                                    className="px-2 py-0.5 text-[10px] font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition whitespace-nowrap"
                                                >
                                                    {copiedRow === inv.id ? 'Copied!' : 'Copy'}
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-slate-300 text-xs">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{fmtExpiry(inv.expires_at)}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${inviteStatusStyle(inv.status)}`}>
                                            {inv.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                                        {new Date(inv.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3">
                                        {inv.status === 'pending' && (
                                            <button
                                                onClick={() => setRevokeConfirm(inv)}
                                                className="text-xs font-semibold text-red-500 hover:text-red-700 transition"
                                            >
                                                Revoke
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Generate invite confirmation modal */}
            {showInviteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowInviteConfirm(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6">
                        <h3 className="text-sm font-bold text-slate-800 mb-3">Generate invite link?</h3>
                        <div className="space-y-2 mb-5">
                            <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                                <span className="text-slate-400 text-xs font-semibold">Email</span>
                                <p className="text-slate-800 font-medium">{email || <span className="italic text-slate-400">Generic (no email)</span>}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                                <span className="text-slate-400 text-xs font-semibold">Plan</span>
                                <p className="text-slate-800 font-medium capitalize">{planTier}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                                <span className="text-slate-400 text-xs font-semibold">Expires</span>
                                <p className="text-slate-800 font-medium">{EXPIRY_OPTIONS.find(o => o.value === expiryDays)?.label || `${expiryDays} days`}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 justify-end">
                            <button onClick={() => setShowInviteConfirm(false)} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition">
                                Cancel
                            </button>
                            <button onClick={handleGenerateConfirmed} className="px-4 py-2 text-sm font-bold text-white bg-[#895129] hover:bg-[#660F09] rounded-lg transition">
                                Generate invite
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Revoke invite confirmation */}
            <ConfirmDialog
                open={!!revokeConfirm}
                title="Revoke invite?"
                body={`Revoke the invite for "${revokeConfirm?.email || 'generic link'}"? The link will stop working immediately.`}
                confirmLabel="Revoke"
                onConfirm={handleRevokeConfirmed}
                onCancel={() => setRevokeConfirm(null)}
            />
        </div>
    );
};

/* ── QuotaRequestsSection ────────────────────────────────────────────────── */
const qrStatusStyle = (status) => {
    switch (status) {
        case 'approved': return 'text-green-700 bg-green-50';
        case 'denied':   return 'text-red-600 bg-red-50';
        default:         return 'text-amber-700 bg-amber-50';
    }
};

const QuotaRequestsSection = ({ token, addNotification }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reviewing, setReviewing] = useState({});
    const [reviewConfirm, setReviewConfirm] = useState(null); // { id, status, username, requested }

    const loadRequests = useCallback(() => {
        api.listQuotaRequests()
            .then(data => setRequests(data.requests ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => { loadRequests(); }, [loadRequests]);

    const handleReviewConfirmed = async () => {
        const { id, status } = reviewConfirm;
        setReviewConfirm(null);
        setReviewing(r => ({ ...r, [id]: status }));
        try {
            await api.reviewQuotaRequest(id, status);
            setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
            addNotification?.({ type: 'success', title: `Request ${status}`, message: `Quota request has been ${status}.` });
            loadRequests();
        } catch {
            addNotification?.({ type: 'error', title: 'Review failed', message: 'Could not update the request.' });
        } finally {
            setReviewing(r => ({ ...r, [id]: null }));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600" />
            </div>
        );
    }

    if (requests.length === 0) {
        return <p className="text-sm text-slate-400 text-center py-8">No quota requests.</p>;
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-100 bg-slate-50">
                        <th className="px-4 py-3">User</th>
                        <th className="px-4 py-3">Current Quota</th>
                        <th className="px-4 py-3">Requested</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {requests.map(r => (
                        <tr key={r.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3">
                                <div>
                                    <p className="font-semibold text-slate-800">{r.user?.username ?? '—'}</p>
                                    <p className="text-xs text-slate-400">{r.user?.email ?? ''}</p>
                                </div>
                            </td>
                            <td className="px-4 py-3 text-slate-600">
                                {r.user?.quota_max_bytes > 0 ? fmt(r.user.quota_max_bytes) : '—'}
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-800">
                                {(r.requested_bytes / (1024 ** 3)).toFixed(1)} GB
                            </td>
                            <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                                {new Date(r.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3">
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${qrStatusStyle(r.status)}`}>
                                    {r.status}
                                </span>
                            </td>
                            <td className="px-4 py-3">
                                {r.status === 'pending' ? (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setReviewConfirm({ id: r.id, status: 'approved', username: r.user?.username, requested: r.requested_bytes })}
                                            disabled={!!reviewing[r.id]}
                                            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition"
                                        >
                                            {reviewing[r.id] === 'approved' ? 'Approving…' : 'Approve'}
                                        </button>
                                        <button
                                            onClick={() => setReviewConfirm({ id: r.id, status: 'denied', username: r.user?.username, requested: r.requested_bytes })}
                                            disabled={!!reviewing[r.id]}
                                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition"
                                        >
                                            {reviewing[r.id] === 'denied' ? 'Denying…' : 'Deny'}
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-xs text-slate-400">—</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Quota review confirmation */}
            <ConfirmDialog
                open={!!reviewConfirm}
                title={reviewConfirm?.status === 'approved' ? 'Approve quota request?' : 'Deny quota request?'}
                body={reviewConfirm?.status === 'approved'
                    ? `Approve ${reviewConfirm?.username}'s request for ${((reviewConfirm?.requested || 0) / (1024 ** 3)).toFixed(1)} GB? Their quota will be updated.`
                    : `Deny ${reviewConfirm?.username}'s request for ${((reviewConfirm?.requested || 0) / (1024 ** 3)).toFixed(1)} GB?`
                }
                confirmLabel={reviewConfirm?.status === 'approved' ? 'Approve' : 'Deny'}
                confirmClass={reviewConfirm?.status === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                onConfirm={handleReviewConfirmed}
                onCancel={() => setReviewConfirm(null)}
            />
        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════════════
   EmailSuppressionsSection
   ════════════════════════════════════════════════════════════════════════════ */
const EmailSuppressionsSection = ({ addNotification }) => {
    const [suppressions, setSuppressions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removeConfirm, setRemoveConfirm] = useState(null); // suppression object or null

    const load = useCallback(() => {
        api.listEmailSuppressions()
            .then(data => setSuppressions(data.suppressions ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleRemoveConfirmed = async () => {
        const s = removeConfirm;
        setRemoveConfirm(null);
        try {
            await api.deleteEmailSuppression(s.id);
            setSuppressions(prev => prev.filter(x => x.id !== s.id));
            addNotification?.({ type: 'success', title: 'Suppression removed', message: `${s.email} can receive email again.` });
        } catch {
            addNotification?.({ type: 'error', title: 'Failed', message: 'Could not remove suppression.' });
        }
    };

    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-500">
                Addresses added here will never receive invite emails. Records are written automatically when SES reports a bounce or complaint.
                You can manually remove an entry to unblock an address.
            </p>

            {loading ? (
                <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600" />
                </div>
            ) : suppressions.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No suppressed addresses.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-100 bg-slate-50">
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Reason</th>
                                <th className="px-4 py-3">Detail</th>
                                <th className="px-4 py-3">Suppressed At</th>
                                <th className="px-4 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {suppressions.map(s => (
                                <tr key={s.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{s.email}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${
                                            s.reason === 'bounce'
                                                ? 'bg-orange-50 text-orange-600'
                                                : 'bg-red-50 text-red-600'
                                        }`}>
                                            {s.reason}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-400">{s.detail || '—'}</td>
                                    <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
                                        {new Date(s.created_at).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => setRemoveConfirm(s)}
                                            className="text-xs font-semibold text-red-500 hover:text-red-700 transition"
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmDialog
                open={removeConfirm !== null}
                title="Remove suppression?"
                body={`Allow "${removeConfirm?.email}" to receive emails again? Only do this if you're sure the address is valid and the bounce/complaint was erroneous.`}
                confirmLabel="Remove"
                confirmClass="bg-red-600 hover:bg-red-700"
                onConfirm={handleRemoveConfirmed}
                onCancel={() => setRemoveConfirm(null)}
            />
        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════════════
   AdminPage
   ════════════════════════════════════════════════════════════════════════════ */
const TABS = [
    { key: 'users',        label: 'Users' },
    { key: 'deletions',    label: 'Pending Deletions' },
    { key: 'invites',      label: 'Invites' },
    { key: 'requests',     label: 'Requests' },
    { key: 'suppressions', label: 'Email Suppressions' },
];

const AdminPage = ({ token, user, addNotification }) => {
    const [activeTab, setActiveTab] = useState('users');
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Per-row state keyed by user ID
    const [pendingPlan, setPendingPlan]   = useState({});
    const [pendingQuota, setPendingQuota] = useState({});
    const [saving, setSaving]             = useState({});
    const [deleting, setDeleting]         = useState({});

    // Confirm dialogs
    const [confirm, setConfirm]           = useState({ open: false, user: null });
    const [confirmSaveAll, setConfirmSaveAll] = useState(false);
    const [savingAll, setSavingAll]           = useState(false);


    const loadData = useCallback(() => {
        Promise.all([
            api.getStats(),
            api.listUsers(),
        ]).then(([s, u]) => {
            setStats(s);
            setUsers(u.users ?? []);
        }).catch(() => {}).finally(() => setLoading(false));
    }, [token]);

    useEffect(() => { loadData(); }, [loadData]);

    const getQuotaGB = (u) => u.quota_max_bytes > 0 ? String(Math.floor(u.quota_max_bytes / (1024 ** 3))) : '';

    const isDirty = (u) => {
        const quotaGB      = getQuotaGB(u);
        const planChanged  = pendingPlan[u.id]  !== undefined && pendingPlan[u.id]  !== u.plan_tier;
        const quotaChanged = pendingQuota[u.id] !== undefined && pendingQuota[u.id] !== quotaGB;
        return planChanged || quotaChanged;
    };

    // Returns list of human-readable change strings for a user
    const getChanges = (u) => {
        const changes = [];
        const quotaGB = getQuotaGB(u);
        if (pendingPlan[u.id] !== undefined && pendingPlan[u.id] !== u.plan_tier) {
            const from = u.plan_tier || '—';
            const to   = pendingPlan[u.id];
            changes.push({ label: 'Plan', from: from.charAt(0).toUpperCase() + from.slice(1), to: to.charAt(0).toUpperCase() + to.slice(1) });
        }
        if (pendingQuota[u.id] !== undefined && pendingQuota[u.id] !== quotaGB) {
            changes.push({ label: 'Quota', from: quotaGB ? `${quotaGB} GB` : '—', to: `${pendingQuota[u.id]} GB` });
        }
        return changes;
    };

    const handleSave = async (u) => {
        setSaving(s => ({ ...s, [u.id]: true }));
        try {
            const planVal      = pendingPlan[u.id]  ?? u.plan_tier;
            const quotaGB      = getQuotaGB(u);
            const planChanged  = pendingPlan[u.id]  !== undefined && pendingPlan[u.id]  !== u.plan_tier;
            const quotaChanged = pendingQuota[u.id] !== undefined && pendingQuota[u.id] !== quotaGB;

            await Promise.all([
                planChanged  ? api.updateUserPlan(u.id, planVal) : Promise.resolve(),
                quotaChanged ? api.updateAdminUserQuotaMax(u.id, Math.floor(parseFloat(pendingQuota[u.id]) * 1024 ** 3)) : Promise.resolve(),
            ]);

            const newQuotaMax = quotaChanged
                ? Math.floor(parseFloat(pendingQuota[u.id]) * 1024 ** 3)
                : (planChanged ? (PLAN_QUOTA_GB[planVal] ?? 0) * 1024 ** 3 : null);
            setUsers(prev => prev.map(row => row.id !== u.id ? row : {
                ...row,
                plan_tier:       planChanged  ? planVal : row.plan_tier,
                quota_max_bytes: newQuotaMax !== null ? newQuotaMax : row.quota_max_bytes,
                storage_quota_bytes: (planChanged && !quotaChanged)
                    ? (PLAN_QUOTA_GB[planVal] ?? 0) * 1024 ** 3
                    : row.storage_quota_bytes,
            }));
            setPendingPlan(p  => { const n = { ...p };  delete n[u.id]; return n; });
            setPendingQuota(p => { const n = { ...p };  delete n[u.id]; return n; });
            addNotification?.({ type: 'success', title: 'Saved', message: `${u.username} updated.` });
        } catch {
            addNotification?.({ type: 'error', title: 'Save failed', message: `Could not update ${u.username}.` });
        } finally {
            setSaving(s => ({ ...s, [u.id]: false }));
        }
    };

    const handleSaveAll = async () => {
        setConfirmSaveAll(false);
        setSavingAll(true);
        const dirty = users.filter(u => isDirty(u));
        await Promise.all(dirty.map(u => handleSave(u)));
        setSavingAll(false);
    };

    const requestDelete = (u) => setConfirm({ open: true, user: u });

    const handleDeleteConfirmed = async () => {
        const u = confirm.user;
        setConfirm({ open: false, user: null });
        setDeleting(d => ({ ...d, [u.id]: true }));
        try {
            const data = await api.deleteUser(u.id);
            // Update user in list to show pending deletion badge
            setUsers(prev => prev.map(row =>
                row.id === u.id ? { ...row, scheduled_deletion_at: data.scheduled_deletion_at } : row
            ));
            addNotification?.({ type: 'success', title: 'Deletion scheduled', message: `${u.username} will be deleted in 30 days.` });
        } catch (err) {
            addNotification?.({ type: 'error', title: 'Delete failed', message: err.message || `Could not schedule deletion for ${u.username}.` });
        } finally {
            setDeleting(d => ({ ...d, [u.id]: false }));
        }
    };

    const totalUsers = users.length;

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            {/* Delete user confirm */}
            <ConfirmDialog
                open={confirm.open}
                title="Schedule account deletion"
                body={`Schedule "${confirm.user?.username}" for deletion? They will be locked out immediately. After 30 days, all their files and data will be permanently removed. You can cancel this from the Pending Deletions tab.`}
                confirmLabel="Schedule deletion"
                onConfirm={handleDeleteConfirmed}
                onCancel={() => setConfirm({ open: false, user: null })}
            />

            {/* Save all confirm */}
            {confirmSaveAll && (() => {
                const dirty = users.filter(u => isDirty(u));
                return (
                    <ConfirmDialog
                        open={true}
                        title={`Apply ${dirty.length} change${dirty.length !== 1 ? 's' : ''}?`}
                        confirmLabel="Apply all"
                        confirmClass="bg-[#895129] hover:bg-[#660F09]"
                        onConfirm={handleSaveAll}
                        onCancel={() => setConfirmSaveAll(false)}
                        body={
                            <div className="space-y-3">
                                {dirty.map(u => (
                                    <div key={u.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
                                        <p className="text-xs font-bold text-slate-700 mb-1.5">{u.username}</p>
                                        <div className="space-y-1">
                                            {getChanges(u).map((c, i) => (
                                                <div key={i} className="flex items-center gap-2 text-xs">
                                                    <span className="text-slate-400 w-10">{c.label}</span>
                                                    <span className="text-slate-500 line-through">{c.from}</span>
                                                    <svg className="w-3 h-3 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                    <span className="font-semibold" style={{ color: '#660F09' }}>{c.to}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        }
                    />
                );
            })()}

            {/* ── Header ──────────────────────────────────────────────────── */}
            <header className="bg-gradient-to-r from-slate-800 to-slate-600 text-white shadow-lg">
                <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </Link>
                        <div>
                            <h1 className="text-lg font-bold">Admin Console</h1>
                            <p className="text-slate-300 text-xs">{user?.email}</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-7xl w-full mx-auto px-3 py-5 sm:px-6 sm:py-8 space-y-6">

                {/* ── Stats bar ────────────────────────────────────────────── */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                        { label: 'Total Users',   value: loading ? '…' : totalUsers },
                        { label: 'Total Files',   value: loading ? '…' : (stats?.total_files ?? '—') },
                        { label: 'Total Storage', value: loading ? '…' : fmt(stats?.total_size_bytes ?? 0) },
                        { label: 'Database',      value: loading ? '…' : (stats?.database ?? '—') },
                    ].map(({ label, value }) => (
                        <div key={label} className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4">
                            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">{label}</p>
                            <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
                        </div>
                    ))}
                </div>

                {/* ── Tab bar ──────────────────────────────────────────────── */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="flex overflow-x-auto whitespace-nowrap border-b border-slate-100">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-6 py-3 text-sm font-semibold transition border-b-2 ${
                                    activeTab === tab.key
                                        ? 'text-slate-800 border-slate-700'
                                        : 'text-slate-400 border-transparent hover:text-slate-600 hover:border-slate-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="px-3 py-4 sm:px-6 sm:py-5">
                        {/* ── Users tab ────────────────────────────────────── */}
                        {activeTab === 'users' && (
                            <div className="space-y-6">
                                {loading ? (
                                    <div className="p-10 flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" />
                                    </div>
                                ) : users.length === 0 ? (
                                    <div className="p-8 text-center text-slate-400 text-sm">No other users found.</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-100 bg-slate-50">
                                                    <th className="px-6 py-3">User</th>
                                                    <th className="px-4 py-3">Plan</th>
                                                    <th className="px-4 py-3 min-w-[140px]">Storage</th>
                                                    <th className="px-4 py-3">Quota Max</th>
                                                    <th className="px-4 py-3">Files</th>
                                                    <th className="px-4 py-3">Joined</th>
                                                    <th className="px-4 py-3">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {users.map(u => {
                                                    const quotaGB      = u.quota_max_bytes > 0 ? String(Math.floor(u.quota_max_bytes / (1024 ** 3))) : '';
                                                    const currentPlan  = pendingPlan[u.id]  ?? u.plan_tier;
                                                    const currentQuota = pendingQuota[u.id] ?? quotaGB;
                                                    const dirty        = isDirty(u);
                                                    const isSaving     = saving[u.id]   ?? false;
                                                    const isDeleting   = deleting[u.id] ?? false;

                                                    return (
                                                        <tr key={u.id} className={`transition ${dirty ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-slate-50'}`}>
                                                            <td className="px-6 py-3">
                                                                <div className="flex items-center gap-2">
                                                                    {dirty && <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" title="Unsaved changes" />}
                                                                    <div>
                                                                        <div className="flex items-center gap-2">
                                                                            <p className={`font-semibold ${u.scheduled_deletion_at ? 'text-red-400 line-through' : 'text-slate-800'}`}>{u.username}</p>
                                                                            {u.scheduled_deletion_at && (
                                                                                <span className="text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">Pending deletion</span>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-xs text-slate-400">{u.email}</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <Dropdown
                                                                    value={currentPlan}
                                                                    onChange={v => {
                                                                        setPendingPlan(p => ({ ...p, [u.id]: v }));
                                                                        setPendingQuota(q => ({ ...q, [u.id]: String(PLAN_QUOTA_GB[v] ?? '') }));
                                                                    }}
                                                                    options={PLAN_OPTIONS}
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <StorageBar
                                                                    compact
                                                                    used={u.storage_used_bytes}
                                                                    quota={u.storage_quota_bytes}
                                                                    max={u.quota_max_bytes}
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-center gap-1.5">
                                                                    <input
                                                                        type="number"
                                                                        min={0}
                                                                        value={currentQuota}
                                                                        onChange={e => setPendingQuota(p => ({ ...p, [u.id]: e.target.value }))}
                                                                        placeholder="—"
                                                                        className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-slate-500"
                                                                    />
                                                                    <span className="text-xs text-slate-400">GB</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-slate-600">{u.file_count}</td>
                                                            <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                                                                {new Date(u.created_at).toLocaleDateString()}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={() => handleSave(u)}
                                                                        disabled={isSaving || !dirty}
                                                                        className={`px-3 py-1.5 bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition ${!dirty ? 'invisible' : ''}`}
                                                                    >
                                                                        {isSaving ? 'Saving…' : 'Save'}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => requestDelete(u)}
                                                                        disabled={isDeleting}
                                                                        className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                                                                        title="Delete user"
                                                                    >
                                                                        {isDeleting
                                                                            ? <div className="w-4 h-4 border-b border-red-400 rounded-full animate-spin" />
                                                                            : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                            </svg>
                                                                        }
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Register form */}
                                <div className="border-t border-slate-100 pt-5">
                                    <h3 className="text-sm font-bold text-slate-800 mb-3">Register New User</h3>
                                    <RegisterUserForm token={token} addNotification={addNotification} />
                                </div>
                            </div>
                        )}

                        {/* ── Pending Deletions tab ────────────────────────── */}
                        {activeTab === 'deletions' && (
                            <PendingDeletionsSection addNotification={addNotification} />
                        )}

                        {/* ── Invites tab ──────────────────────────────────── */}
                        {activeTab === 'invites' && (
                            <InvitesSection token={token} addNotification={addNotification} />
                        )}

                        {/* ── Requests tab ─────────────────────────────────── */}
                        {activeTab === 'requests' && (
                            <QuotaRequestsSection token={token} addNotification={addNotification} />
                        )}

                        {/* ── Email Suppressions tab ────────────────────────── */}
                        {activeTab === 'suppressions' && (
                            <EmailSuppressionsSection addNotification={addNotification} />
                        )}
                    </div>
                </div>

            </main>

            {/* ── Floating save-all bar ────────────────────────────────────── */}
            {(() => {
                const dirtyCount = users.filter(u => isDirty(u)).length;
                if (dirtyCount === 0) return null;
                return (
                    <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-4 text-white rounded-2xl shadow-2xl px-6 py-3.5"
                        style={{ background: 'linear-gradient(to right, #660F09, #895129)' }}>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-400" />
                            <span className="text-sm font-semibold">
                                {dirtyCount} user{dirtyCount !== 1 ? 's' : ''} with unsaved changes
                            </span>
                        </div>
                        <div className="w-px h-4 bg-white/30" />
                        <button
                            onClick={() => setConfirmSaveAll(true)}
                            disabled={savingAll}
                            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 disabled:opacity-50 px-4 py-1.5 rounded-xl text-sm font-bold transition"
                        >
                            {savingAll
                                ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            }
                            Save all changes
                        </button>
                        <button
                            onClick={() => { setPendingPlan({}); setPendingQuota({}); }}
                            className="text-white/60 hover:text-white text-xs font-medium transition"
                        >
                            Discard
                        </button>
                    </div>
                );
            })()}
        </div>
    );
};

export default AdminPage;
